import { useCallback, useMemo } from 'react';
import { CallBadge } from './CallBadge';
import { reasoningTooltip } from './ReasoningBadge';
import { extractParts, partsContextPictures, partsPrompt, partsReadPictures, usePartsRun } from './partsRun';
import type { Bands } from './categoryBands';
import { useLanguage } from '../../contexts/LanguageContext';

// EVERYTHING THE EXTRACTION HAS TO SAY: the prompt it sends, and what came back.
//
// The call is the one behind this phase's own button — read the supplied-parts section and
// return one entry per part. Its answer used to hang off the RIGHT EDGE OF THE TABLE, as a
// lone brain with no prompt beside it, which made it the only call in the app whose two halves
// were not one badge and whose badge was nowhere near the button that fires it. A reader
// hovering it had no way to know which of Phase 2's eight calls it was about.
//
// So it is here, beside the run button, next to the size read's own badge — see DimensionsInfo,
// which this is deliberately shaped like, and PartsPhase, where the two sit side by side in the
// order they run. Same argument as CallBadge's: one call, one icon, a menu of two.
//
// ONE BADGE FOR A CALL MADE ONCE PER PAGE. The extraction runs per supplied-parts page, so the
// answer is a section per page — and the heading is drawn only when there are several, since
// "Page 4" over the single page a manual has is a heading over nothing.
export function PartsListInfo({ docId, file, bands, disabled, onStop }: {
  docId: string;
  // The manual itself, and which of its pages this phase reads. The prompt comes from the
  // server; the PICTURES are rendered here out of these two, because they are rendered here
  // when the button is pressed — see partsReadPictures.
  file: Blob;
  bands: Bands;
  // Something else is in flight — the whole press — or the phase is locked. See CallRun.
  disabled: boolean;
  // STOP, while this badge's run is out and was started on its own (see CallRun.onStop).
  onStop?: () => void;
}) {
  const { t } = useLanguage();
  const { items, reasoning, raw, busy } = usePartsRun(docId);
  // EVERY PICTURE, IN THE ORDER THE CALL SENDS THEM: the manual's pages as context, and the
  // supplied-parts section LAST, nearest the prompt — which is where the prompt's own first
  // sentence points ("The LAST image is the supplied parts section"). See the parts route,
  // which builds the list this way and captions each one.
  //
  // They went the other way round at first — the section first, the context after it — on the
  // argument that the section is one render and the context is twenty-four, so the cheap half
  // should land with the words. Which put the panel in flat contradiction with the prompt
  // printed above it, and a preview that argues with the call is worse than no preview.
  //
  // All of them behind ONE loader, then, and not the split: the words arrive in a round trip
  // and every picture here is a render, so the split is between the text and the pictures, not
  // between two kinds of picture.
  const load = partsPrompt;
  const more = useCallback(async () => {
    const [context, read] = await Promise.all([
      partsContextPictures(file, t),
      partsReadPictures(file, bands, t),
    ]);
    return [...context, ...read];
  }, [file, bands, t]);

  const answer = useMemo(() => {
    // The pages that actually said something, and not every page a part was found on: a page
    // whose reasoning and raw are both empty contributes a heading and a blank.
    const said = [...new Set(items.map((p) => p.page))]
      .sort((a, b) => a - b)
      .filter((pg) => reasoning[pg] || raw[pg]);
    if (busy || !said.length) return undefined;
    return {
      label: t('notice.cat.thinking'),
      content: (
        <div className="space-y-4">
          {said.map((pg) => (
            <div key={pg}>
              {said.length > 1 && (
                <div className="mb-1 font-semibold">
                  {t('notice.pdf.page')} {pg}
                </div>
              )}
              {/* Reasoning AND raw output: the raw is the fallback for when the reasoning
                  explains nothing. */}
              {reasoningTooltip(reasoning[pg] || '', raw[pg] || '', t)}
            </div>
          ))}
        </div>
      ),
    };
  }, [items, reasoning, raw, busy, t]);

  // The prompt needs no run to exist, so the badge is always here — before the first press it
  // is a menu of one and opens the prompt directly, which is exactly what somebody deciding
  // whether to spend a call wants to read.
  return (
    <CallBadge
      title={t('notice.parts.badgeList')}
      named
      // THE LIST ON ITS OWN, without the sizes, crops and materials the phase's button makes
      // after it — the way the Size badge runs its own call. Every part, whatever is open.
      run={{
        onRun: () => void extractParts(docId, file, bands, t),
        busy,
        disabled,
        onStop,
      }}
      promptLabel={t('notice.parts.listPrompt')}
      load={load}
      more={more}
      answer={answer}
    />
  );
}
